import { create } from "zustand";

import { clearLocalStorageValues } from "@lib/hooks";
import container from "@src/container";
import { clearPrefsCache } from "@views/outcomes/prefs/outcomes-prefs-cache";

import { useAiCurriculumUIStore } from "./ai-curriculum-ui.store";
import { useOutcomesUIStore } from "./outcomes-ui.store";

const { proProfsCache } = container;
const proProfsCacheData = proProfsCache.get();

/**
 * Minimal UI store for account-related transient state
 * Server state (currentUser) is managed by react-query
 */
export const useAccountUIStore = create((set, get) => ({
  // UI state only
  token: undefined,
  impersonating: !!proProfsCacheData?.impersonation,
  bulkUploadId: undefined,
  proProfsSettings: proProfsCacheData,
  selfSignupData: undefined,

  // Actions
  setBulkUploadId: (id) => set({ bulkUploadId: id }),

  setImpersonating: (value) => set({ impersonating: value }),

  setLogOutRequested: (logOutRequested) => {
    const proProfsSettings = {
      redirectUrl: undefined,
      appLogoutRequested: logOutRequested,
      ssoSourceUrl: undefined,
    };
    proProfsCache.set(proProfsSettings);
    set({ proProfsSettings });
  },

  clearLogOutRequested: () => {
    const { proProfsSettings } = get();
    const updated = {
      ...proProfsSettings,
      appLogoutRequested: undefined,
    };
    proProfsCache.set(updated);
    set({ proProfsSettings: updated });
  },

  setRedirectUrl: (url) => {
    const { proProfsSettings } = get();
    const updated = { ...proProfsSettings, redirectUrl: url };
    proProfsCache.set(updated);
    set({ proProfsSettings: updated });
  },

  setSsoSourceUrl: (url) => {
    const { proProfsSettings } = get();
    const updated = { ...proProfsSettings, ssoSourceUrl: url };
    proProfsCache.set(updated);
    set({ proProfsSettings: updated });
  },

  setSelfSignupData: (payload) => set({ selfSignupData: payload }),

  clearStore: () => {
    container.currentUserCache.remove();
    container.tokenCache.remove();
    container.appSettingsCache.remove();
    clearLocalStorageValues();
    clearPrefsCache();
    useOutcomesUIStore.getState().clearStore();
    useAiCurriculumUIStore.getState().clearStore();
    // Teardown pendo to avoid tracking events after logout
    if (typeof pendo !== "undefined") {
      pendo?.teardown?.();
    }
    set({
      impersonating: false,
    });
  },

  doProProfsLogout: (url) => {
    window.location.href = `${url}/user/auth/logout`;
  },
}));
